import { Dispatch, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { UnknownAction } from '@reduxjs/toolkit';

import {
    selectUrlParamsInitialized,
    setEmissionsDrawerCardData,
    toggleAllEmissionChips,
    removeAllMapFeatureHighlights,
    initialDrawerState,
    resetSelectedFeatureParams
} from '../../../reducers';

import {
    CardUrlState,
    EmissionDrawerCardData,
    isOGIInfo,
    isTargetInfo,
    MSatPickInfo,
    TargetInfo,
    UrlSelectionTypes,
    OGIInfo,
    SnapPointIds
} from '../../../types';
import { formatDateForUrl } from '../../../utils';

import { centroid, polygon } from '@turf/turf';

// send drawer card data to redux for use in the URL
export function useDrawers({
    drawerCardData,
    dispatch
}: {
    drawerCardData: (EmissionDrawerCardData | OGIInfo | TargetInfo)[] | null;
    dispatch: Dispatch<UnknownAction>;
}) {
    const urlParamsInitialized = useSelector(selectUrlParamsInitialized);

    useEffect(() => {
        if (drawerCardData && urlParamsInitialized) {
            const emissionsCards: (EmissionDrawerCardData | TargetInfo)[] = drawerCardData.filter(
                (cardData) => !isOGIInfo(cardData as MSatPickInfo)
            ) as (EmissionDrawerCardData | TargetInfo)[];

            const cardState = {} as CardUrlState;

            if (emissionsCards.length > 0) {
                const targetIds: CardUrlState['targets'] = [];
                let selectedLng: number | undefined;
                let selectedLat: number | undefined;

                // multiple cards, all of one type, but eventually they might be of different types
                emissionsCards.forEach((cardData) => {
                    let targetId;
                    let targetDate;
                    let lat: number;
                    let lng: number;
                    if ('object' in cardData && isTargetInfo(cardData)) {
                        // type TargetInfo; opened a target drawer
                        targetId = cardData.object.properties.target_id;
                        targetDate = cardData.object.properties.start_datetime;
                        cardState['selection-type'] = UrlSelectionTypes.Target;

                        // this will need to be changed when we switch to using consistent target bounds
                        [lng, lat] = centroid(polygon([cardData.object.geometry.coordinates[0]])).geometry.coordinates;
                    } else {
                        // must be of type EmissionDrawerCardData; opened an emissions drawer
                        // with (potentially) multiple cards & multiple accordions per card
                        targetId = cardData.targetId;
                        targetDate = cardData.startDateTime;
                        cardState['selection-type'] = UrlSelectionTypes.Details;

                        [lng, lat] = cardData.selectedCoordinates || [];

                        // Use a function for hasCoordinates because lng/lat can be reassigned below.
                        // Each call re-checks the latest values.
                        const hasCoordinates = () => Number.isFinite(lng) && Number.isFinite(lat);
                        if (!hasCoordinates() && cardData.dispersedSourceEmissions.length) {
                            // empty array for total-only card
                            [lng, lat] = cardData.dispersedSourceEmissions[0].coordinate || [];
                        }

                        if (!hasCoordinates() && cardData.distinctPointEmissions.length) {
                            [lng, lat] = cardData.distinctPointEmissions[0].coordinate;
                        }

                        // add points to card state
                        if (cardData.distinctPointEmissions.length) {
                            cardState[`target-${targetId}-points`] = cardData.distinctPointEmissions.map(
                                (pointInfo) => {
                                    return pointInfo.object.properties.plume_id.toString();
                                }
                            );
                        }

                        // Preserve the capture item and collection IDs for this card.
                        if (cardData.dispersedSourceEmissions.length) {
                            cardState[`target-${targetId}-itemId`] = cardData.dispersedSourceEmissions[0].object.id;
                            cardState[`target-${targetId}-collection`] =
                                cardData.dispersedSourceEmissions[0].object.collection;
                        } else if (cardData.distinctPointEmissions.length) {
                            cardState[`target-${targetId}-itemId`] =
                                cardData.distinctPointEmissions[0].object.relatedRasterItemId ||
                                cardData.distinctPointEmissions[0].object.itemId;
                        }

                        // Fallback to the card's preserved capture item id for unavailable cards.
                        if (!cardState[`target-${targetId}-itemId`] && typeof cardData.itemId === 'string') {
                            cardState[`target-${targetId}-itemId`] = cardData.itemId;
                        }
                    }

                    if (
                        Number.isFinite(lng) &&
                        Number.isFinite(lat) &&
                        selectedLng === undefined &&
                        selectedLat === undefined
                    ) {
                        selectedLng = lng;
                        selectedLat = lat;
                    }

                    targetIds.push(targetId);

                    cardState[`target-${targetId}-date`] = formatDateForUrl(new Date(targetDate));
                });
                cardState.targets = targetIds;
                if (selectedLng !== undefined && selectedLat !== undefined) {
                    cardState['selected-lng'] = selectedLng;
                    cardState['selected-lat'] = selectedLat;
                }
            }

            dispatch(setEmissionsDrawerCardData(cardState));
        }
    }, [drawerCardData, dispatch, urlParamsInitialized]);
}

export function useOpenDrawer({
    drawerDispatch,
    dispatch,
    setActiveCardIndex,
    analytics
}: {
    drawerDispatch: Dispatch<
        | { type: 'open'; id: keyof typeof initialDrawerState }
        | { type: 'close' }
        | { type: 'setBottomDrawerSnapPoint'; snapPoint: SnapPointIds }
    >;
    dispatch: Dispatch<UnknownAction>;
    setActiveCardIndex: (index: number) => void;
    analytics: {
        openNoData: () => void;
        openDownload: () => void;
        openSettings: () => void;
    };
}) {
    return useCallback(
        (selectedDrawer: keyof typeof initialDrawerState) => {
            drawerDispatch({ type: 'open', id: selectedDrawer });
            setActiveCardIndex(0);
            dispatch(toggleAllEmissionChips(false));
            if (!['emissionsDetails', 'ogiData', 'targetData'].includes(selectedDrawer))
                dispatch(removeAllMapFeatureHighlights());
            switch (selectedDrawer) {
                case 'noData':
                    return analytics.openNoData();
                case 'download':
                    return analytics.openDownload();
                case 'settings':
                    return analytics.openSettings();
            }
        },
        [drawerDispatch, dispatch, setActiveCardIndex, analytics]
    );
}

/**
 * Closes a side drawer.
 */
export function useCloseDrawer({
    drawerDispatch,
    dispatch
}: {
    drawerDispatch: Dispatch<
        | { type: 'open'; id: keyof typeof initialDrawerState }
        | { type: 'close' }
        | { type: 'setBottomDrawerSnapPoint'; snapPoint: SnapPointIds }
    >;
    dispatch: Dispatch<UnknownAction>;
}) {
    return useCallback(() => {
        dispatch(removeAllMapFeatureHighlights());
        //Remove selected feature parameters from the URL
        dispatch(resetSelectedFeatureParams());
        drawerDispatch({ type: 'close' });
        drawerDispatch({ type: 'setBottomDrawerSnapPoint', snapPoint: SnapPointIds.preview });
    }, [drawerDispatch, dispatch]);
}

/**
 * Set the snapPoint for the bottom-up drawer
 */
export function useSetHeightBottomDrawer({
    drawerDispatch
}: {
    drawerDispatch: Dispatch<
        | { type: 'open'; id: keyof typeof initialDrawerState }
        | { type: 'close' }
        | { type: 'setBottomDrawerSnapPoint'; snapPoint: SnapPointIds }
    >;
}) {
    return useCallback(
        (snapPoint: SnapPointIds) => {
            drawerDispatch({ type: 'setBottomDrawerSnapPoint', snapPoint });
        },
        [drawerDispatch]
    );
}
